import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation, Trans } from 'react-i18next';
import { useUser, useAuth } from '@clerk/react';
import { useQuery } from '@tanstack/react-query';
import { getPlanDetails, type PlanTier } from '@agentbrush/shared';
import { dismissToast, pushToast } from '../app/toast-store.js';
import { useQuotaQuery } from '../../hooks/use-quota-query.js';
import { useUsageQuery } from '../../hooks/use-usage-query.js';
import { useGenerationsQuery, type GenerationRecord } from '../../hooks/use-generations-query.js';
import { useApiClient } from '../../hooks/use-api-client.js';
import { useGlobalErrorShake } from '../../components/ui/error-shake/error-shake-context.js';
import { Odometer } from '../../components/ui/odometer/odometer.js';
import { Skeleton } from '../../components/ui/skeleton/skeleton.js';
import { TableOfContents } from '../../components/ui/table-of-contents/table-of-contents.js';
import { usePreferencesStore } from '../../stores/preferences.js';
import { formatDate } from '../../lib/format-price.js';
import { QuotaExhaustedToast } from './quota-exhausted-toast.js';
import { PastDueToast } from './past-due-toast.js';
import { buildUsageSeries, type UsageSeriesPoint } from './usage-series.js';
import { line as d3Line, curveBumpX } from 'd3-shape';

const QUOTA_TOAST_ID = 'quota-exhausted';
const PAST_DUE_TOAST_ID = 'past-due';
const QUOTA_UPGRADE_HREF = '/billing?intent=quota-exceeded&recommendedPlan=pro';

const PRESET_DISPLAY_NAME: Record<string, string> = {
  realistic: 'Realistic',
  flat_illustration: 'Flat Illustration',
  pixel_art: 'Pixel Art',
  isometric: 'Isometric',
  logo: 'Logo',
  custom: 'Custom',
  mask_edit: 'Mask edit',
};

const PRESET_TONE: Record<string, string> = {
  realistic: 'bg-accent-lime',
  flat_illustration: 'bg-accent-cyan',
  pixel_art: 'bg-accent-rose',
  isometric: 'bg-accent-blue',
  logo: 'bg-accent-lime/60',
  custom: 'bg-white/[0.18]',
  mask_edit: 'bg-accent-rose/60',
};

// ── Page ─────────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const { t } = useTranslation('dashboard');
  const { user } = useUser();
  const quotaQuery = useQuotaQuery({ polling: true });
  const usageQuery = useUsageQuery();

  const quotaData = quotaQuery.data;
  const shake = useGlobalErrorShake();
  const lastDepletedRef = useRef(false);

  useEffect(() => {
    if (quotaData?.plan && quotaData.quotaState === 'depleted' && quotaData.cycleResetDate) {
      pushToast({
        id: QUOTA_TOAST_ID,
        tone: 'error',
        autoDismissMs: null,
        content: (
          <QuotaExhaustedToast
            cycleResetDate={quotaData.cycleResetDate}
            upgradeHref={QUOTA_UPGRADE_HREF}
            onWait={() => dismissToast(QUOTA_TOAST_ID)}
          />
        ),
      });
      if (!lastDepletedRef.current) {
        lastDepletedRef.current = true;
        shake();
      }
      return;
    }
    lastDepletedRef.current = false;
    dismissToast(QUOTA_TOAST_ID);
  }, [quotaData?.cycleResetDate, quotaData?.plan, quotaData?.quotaState, shake]);

  // Sibling effect for the `past_due` state — Stripe's renewal charge
  // failed and Smart Retries are running. Per Stripe guidance we keep
  // access during this grace window; the toast prompts the user to update
  // their card before entitlement is revoked when Stripe gives up.
  useEffect(() => {
    if (quotaData?.plan && quotaData.quotaState === 'past_due') {
      pushToast({
        id: PAST_DUE_TOAST_ID,
        tone: 'error',
        autoDismissMs: null,
        content: <PastDueToast onDismiss={() => dismissToast(PAST_DUE_TOAST_ID)} />,
      });
      return;
    }
    dismissToast(PAST_DUE_TOAST_ID);
  }, [quotaData?.plan, quotaData?.quotaState]);

  const firstName =
    user?.firstName?.trim() ||
    user?.username ||
    user?.primaryEmailAddress?.emailAddress?.split('@')[0] ||
    '';

  if (quotaQuery.isLoading) {
    return (
      <div
        className="mx-auto max-w-[960px] px-6 py-20 standard:px-8"
        data-testid="dashboard-loading"
        aria-busy="true"
        aria-label={t('loading.plan')}
      >
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-5 h-12 w-[60%]" />
        <Skeleton className="mt-4 h-4 w-[80%] max-w-[480px]" />
        <Skeleton className="mt-12 h-[140px] w-full" />
        <div className="mt-12 grid grid-cols-1 gap-12 standard:grid-cols-2 standard:gap-16">
          <Skeleton className="h-[200px]" />
          <Skeleton className="h-[200px]" />
        </div>
      </div>
    );
  }

  if (quotaQuery.isError || !quotaData) {
    return (
      <div className="mx-auto max-w-[960px] px-6 py-20 standard:px-8" data-testid="error-state">
        <SectionEyebrow>{t('error.planLoad')}</SectionEyebrow>
        <button
          type="button"
          onClick={() => quotaQuery.refetch()}
          className="mt-3 cursor-pointer text-sm text-accent-cyan hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          {t('error.retry')}
        </button>
      </div>
    );
  }

  const hasPlan = !!quotaData.plan;
  const generationsThisCycle = usageQuery.data?.generationsThisCycle ?? 0;
  const hasUsageHistory = usageQuery.data?.hasHistory ?? false;
  const usageDailySeries = usageQuery.data?.dailySeries as UsageSeriesPoint[] | undefined;

  return (
    <div className="flex w-full justify-between gap-12 px-6 pb-32 pt-20 standard:px-10">
      <div className="min-w-0 flex-1 wide:max-w-[960px]">
        {hasPlan ? (
          <>
            <HeroSection
              firstName={firstName}
              plan={quotaData.plan as PlanTier}
              tokenBalance={quotaData.tokenBalance}
              monthlyAllocation={quotaData.monthlyAllocation}
              cycleResetDate={quotaData.cycleResetDate}
            />
            <UsageSection
              generationsThisCycle={generationsThisCycle}
              hasHistory={hasUsageHistory}
              cycleResetDate={quotaData.cycleResetDate}
              {...(usageDailySeries ? { dailySeries: usageDailySeries } : {})}
            />
            <PlanAndDevicesSection
              plan={quotaData.plan as PlanTier}
              cycleResetDate={quotaData.cycleResetDate}
            />
            <LedgerSection />
          </>
        ) : (
          <DashboardEmptyOnboarding firstName={firstName} />
        )}
        <DashboardFooter />
      </div>
      {hasPlan && (
        <TableOfContents
          items={[
            { id: 'overview', label: 'Overview' },
            { id: 'usage', label: 'Usage' },
            { id: 'plan-and-devices', label: 'Plan & Devices' },
            { id: 'ledger', label: 'Recent generations' },
          ]}
        />
      )}
    </div>
  );
}

// ── Tiny shared atoms ────────────────────────────────────────────────────────

function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
      {children}
    </span>
  );
}

function SectionHeader({ title, meta }: { title: React.ReactNode; meta?: React.ReactNode }) {
  return (
    <div className="mb-8 flex items-baseline justify-between gap-4">
      <h2 className="font-mono text-[13px] font-medium uppercase tracking-[0.12em] text-secondary">
        {title}
      </h2>
      {meta && <span className="font-mono text-[12px] text-muted">{meta}</span>}
    </div>
  );
}

function Section({
  children,
  first = false,
  ...rest
}: {
  children: React.ReactNode;
  first?: boolean;
  id?: string;
  'data-testid'?: string;
  'aria-labelledby'?: string;
}) {
  return (
    <section
      className={`scroll-mt-24 ${first ? 'pb-14' : 'border-t border-white/[0.06] py-14'}`}
      {...rest}
    >
      {children}
    </section>
  );
}

// ── Connect-MCP teaser ───────────────────────────────────────────────────────

function ConnectMcpRow() {
  const { t } = useTranslation('dashboard');
  return (
    <div className="mt-5 grid gap-3 border-y border-white/[0.06] py-3.5 standard:grid-cols-[1fr_auto] standard:items-center standard:gap-10">
      <div className="flex flex-col gap-2">
        <span className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          {t('connect.eyebrow')}
        </span>
        <p className="m-0 max-w-[60ch] text-[14px] leading-[1.5] text-secondary">
          {t('connect.lede')}
        </p>
      </div>
      <Link
        to="/getting-started"
        className="inline-flex items-center gap-2 self-start rounded-md border border-border-bright bg-transparent px-3.5 py-2 text-[13px] font-medium text-primary transition-colors hover:bg-white/[0.03] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 standard:self-center"
      >
        {t('connect.ctaGuide')}
        <ArrowRight />
      </Link>
    </div>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────────────

interface HeroProps {
  firstName: string;
  plan: PlanTier;
  tokenBalance: number;
  monthlyAllocation: number;
  cycleResetDate: string | null;
}

function HeroSection({
  firstName,
  plan,
  tokenBalance,
  monthlyAllocation,
  cycleResetDate,
}: HeroProps) {
  const { t } = useTranslation('dashboard');
  const locale = usePreferencesStore((s) => s.locale);

  const hasAllocation = monthlyAllocation > 0;
  const usedTokens = Math.max(0, Math.min(monthlyAllocation, monthlyAllocation - tokenBalance));
  const usedPercent = hasAllocation
    ? Math.min(100, Math.round((usedTokens / monthlyAllocation) * 100))
    : 0;
  const meterTone =
    usedPercent >= 90 ? 'bg-accent-red' : usedPercent >= 75 ? 'bg-accent-rose' : 'bg-accent-lime';

  const daysUntilReset = useDaysUntil(cycleResetDate);
  const digitCount = Math.max(1, String(monthlyAllocation || 1).length);
  const planName = getPlanDetails(plan).name;

  return (
    <Section
      first
      id="overview"
      data-testid="plan-section"
      aria-labelledby="dashboard-hero-heading"
    >
      <SectionEyebrow>{t('eyebrow')}</SectionEyebrow>
      <h1
        id="dashboard-hero-heading"
        className="mt-3 font-sans text-[clamp(2rem,5vw,2.75rem)] font-light leading-[1.05] tracking-[-0.025em] text-primary"
      >
        {firstName ? (
          <>
            {t('hero.greeting')} <em className="not-italic text-muted">{firstName}.</em>
          </>
        ) : (
          <>{t('hero.greetingFallback')}.</>
        )}
      </h1>
      <p className="mt-3 max-w-[60ch] text-[15px] leading-[1.55] text-secondary">
        <Trans
          i18nKey="hero.lede"
          t={t}
          values={{
            plan: planName,
            allocation: monthlyAllocation.toLocaleString(locale),
            days: daysUntilReset,
            date: cycleResetDate ? formatDate(cycleResetDate, locale) : '',
          }}
          components={{ b: <span className="font-medium text-primary" /> }}
        />
      </p>

      <ConnectMcpRow />

      {/* Big tabular token meter */}
      <div aria-live="polite" aria-atomic="true" className="mt-5 flex items-baseline gap-6">
        <span
          className="font-sans tabular-nums text-[clamp(72px,12vw,140px)] font-light leading-[0.9] tracking-[-0.04em] text-primary"
          aria-label={t('plan.tokenBalance')}
        >
          <Odometer value={tokenBalance} minDigitCount={digitCount} />
        </span>
        {hasAllocation && (
          <span className="pb-2 font-mono text-[13px] tracking-[0.04em] text-muted">
            {t('hero.of')}{' '}
            <span className="font-medium text-secondary">
              {monthlyAllocation.toLocaleString(locale)}
            </span>{' '}
            {t('hero.tokensSuffix')}
          </span>
        )}
      </div>

      {hasAllocation && (
        <p className="mt-4 max-w-[560px] text-[14px] text-secondary">
          <Trans
            i18nKey={cycleResetDate ? 'hero.subBalance' : 'hero.subBalanceNoReset'}
            t={t}
            values={{
              remaining: tokenBalance.toLocaleString(locale),
              days: daysUntilReset,
            }}
            components={{ b: <span className="font-medium text-primary" /> }}
          />
        </p>
      )}

      {/* Hairline meter bar */}
      {hasAllocation && (
        <div className="mt-10" data-testid="usage-section">
          <div
            className="relative h-px w-full overflow-hidden bg-white/[0.06]"
            role="progressbar"
            aria-valuenow={usedTokens}
            aria-valuemin={0}
            aria-valuemax={monthlyAllocation}
            aria-label={t('plan.tokenUsage')}
          >
            <div
              className={`h-full transition-[width] duration-700 ease-out ${meterTone}`}
              style={{ width: `${usedPercent}%` }}
            />
          </div>
          <div className="mt-3 flex justify-between font-mono text-[12px] text-muted">
            <span>
              <span className="font-medium text-secondary">
                {usedTokens.toLocaleString(locale)}
              </span>{' '}
              used
            </span>
            {cycleResetDate && (
              <span>{t('hero.footResetsOn', { date: formatDate(cycleResetDate, locale) })}</span>
            )}
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-2">
        <Link
          to="/billing"
          className="inline-flex items-center gap-2 rounded-md bg-accent-lime px-4 py-2.5 text-[13px] font-semibold text-canvas transition-[filter] hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          {t('hero.ctaManage')}
          <ArrowRight />
        </Link>
        <Link
          to="/docs"
          className="inline-flex items-center gap-2 rounded-md border border-border-bright bg-transparent px-4 py-2.5 text-[13px] font-medium text-primary transition-colors hover:bg-white/[0.03] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          {t('hero.ctaDocs')}
        </Link>
      </div>
    </Section>
  );
}

// ── Usage section — only real numbers ────────────────────────────────────────

function UsageSection({
  generationsThisCycle,
  hasHistory,
  cycleResetDate,
  dailySeries,
}: {
  generationsThisCycle: number;
  hasHistory: boolean;
  cycleResetDate: string | null;
  dailySeries?: UsageSeriesPoint[];
}) {
  const { t } = useTranslation('dashboard');
  const locale = usePreferencesStore((s) => s.locale);

  // Real per-day series from the API when present; fall back to the
  // synthesized 30-day distribution. Backend currently returns 14 points,
  // which still looks balanced at full width.
  const series = useMemo(() => {
    if (dailySeries && dailySeries.length > 0) return dailySeries;
    return buildUsageSeries(generationsThisCycle, cycleResetDate, 30);
  }, [dailySeries, generationsThisCycle, cycleResetDate]);

  const total = series.reduce((a, p) => a + p.value, 0);

  return (
    <Section id="usage" data-testid="usage-graph-section" aria-labelledby="usage-section-heading">
      <SectionHeader
        title={<span id="usage-section-heading">{t('usage.section')}</span>}
        meta={t('usage.totalLabel')}
      />
      <div className="font-sans text-[clamp(36px,6vw,52px)] font-light leading-none tracking-[-0.03em] text-primary tabular-nums">
        {generationsThisCycle.toLocaleString(locale)}
      </div>
      <p className="mt-2 font-mono text-[12px] text-muted">generations this cycle</p>
      {hasHistory && (
        <UsageLineGraph
          series={series}
          ariaLabel={t('usage.chartAriaLabel', { days: series.length, total })}
        />
      )}
    </Section>
  );
}

// Minimal flat line graph matching the original dashboard design: hairline
// baseline + mid grid, lime stroke, small end dot, draw-in animation. No
// card chrome — sits flush in the surrounding section.
function UsageLineGraph({ series, ariaLabel }: { series: UsageSeriesPoint[]; ariaLabel: string }) {
  const locale = usePreferencesStore((s) => s.locale);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const w = 960;
  const h = 140;
  const padX = 8;
  const padTop = 20;
  const padBot = 10;
  const max = Math.max(1, ...series.map((p) => p.value));
  const stepX = (w - padX * 2) / Math.max(1, series.length - 1);
  const points: Array<[number, number]> = series.map((p, i) => [
    padX + i * stepX,
    padTop + (h - padTop - padBot) * (1 - p.value / max),
  ]);

  // curveBumpX builds a cubic bezier between each pair of points whose
  // control handles are purely horizontal, so the tangent at every data
  // point is flat — gives the smooth, eased rises/falls the user asked for.
  const pathFn = d3Line<[number, number]>()
    .x((d) => d[0])
    .y((d) => d[1])
    .curve(curveBumpX);
  const d = pathFn(points) ?? '';
  const lastPoint = points[points.length - 1];
  const approxLen = w + h;

  // Even tick spacing along the bottom: ~5 labels regardless of series length.
  const tickCount = Math.min(5, series.length);
  const tickIndices = Array.from({ length: tickCount }, (_, i) =>
    Math.round((i * (series.length - 1)) / Math.max(1, tickCount - 1)),
  );

  // Y-axis ticks: 0, mid, max — three labels on the right edge.
  const yTicks: Array<{ value: number; topPct: number }> = [
    { value: max, topPct: (padTop / h) * 100 },
    { value: Math.round(max / 2), topPct: ((padTop + (h - padTop - padBot) / 2) / h) * 100 },
    { value: 0, topPct: ((h - padBot) / h) * 100 },
  ];

  const hoveredPoint = hoveredIndex !== null ? (series[hoveredIndex] ?? null) : null;
  const hoveredXY: [number, number] | null =
    hoveredIndex !== null ? (points[hoveredIndex] ?? null) : null;
  const tooltipLeftPct = hoveredXY ? Math.max(6, Math.min(94, (hoveredXY[0] / w) * 100)) : 0;

  return (
    <div className="relative mt-6 pr-10">
      <svg
        viewBox={`0 0 ${String(w)} ${String(h)}`}
        className="block w-full"
        role="img"
        aria-label={ariaLabel}
        preserveAspectRatio="none"
      >
        <line
          x1={0}
          x2={w}
          y1={h - padBot}
          y2={h - padBot}
          stroke="currentColor"
          strokeOpacity={0.06}
          className="text-primary"
        />
        <line
          x1={0}
          x2={w}
          y1={(h - padTop - padBot) / 2 + padTop}
          y2={(h - padTop - padBot) / 2 + padTop}
          stroke="currentColor"
          strokeOpacity={0.04}
          className="text-primary"
        />
        <path
          d={d}
          fill="none"
          stroke="var(--color-accent-lime)"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: approxLen,
            strokeDashoffset: approxLen,
            animation: 'agb-draw-line 1.6s cubic-bezier(.4,0,.2,1) 0.1s forwards',
          }}
          className="motion-reduce:[animation:none] motion-reduce:[stroke-dashoffset:0]"
        />
        {/* Per-day section rects — Voronoi-style cells centered on each
            point. Interior cells span one full interval (so the highlight
            renders symmetrically around the point on hover); edge cells are
            half-width so they reach the viewBox boundary. Filled with a
            faint lime tint on hover so the section reads as a column. */}
        {series.map((_, i) => {
          const cx = points[i]?.[0] ?? 0;
          const left = i === 0 ? 0 : cx - stepX / 2;
          const width = i === 0 || i === series.length - 1 ? stepX / 2 + padX : stepX;
          const isHovered = hoveredIndex === i;
          return (
            <rect
              key={i}
              x={left}
              y={0}
              width={width}
              height={h}
              fill={isHovered ? 'var(--color-accent-lime)' : 'transparent'}
              fillOpacity={isHovered ? 0.08 : 0}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex((prev) => (prev === i ? null : prev))}
              aria-hidden="true"
            />
          );
        })}
        {/* Emphasised dot at the hovered point — sits on top of the
            column highlight so the exact data location remains visible. */}
        {hoveredXY && (
          <circle cx={hoveredXY[0]} cy={hoveredXY[1]} r={4} fill="var(--color-accent-lime)" />
        )}
        {/* End-of-series dot when nothing is hovered. */}
        {lastPoint && hoveredIndex === null && (
          <circle
            cx={lastPoint[0]}
            cy={lastPoint[1]}
            r={3}
            fill="var(--color-accent-lime)"
            opacity={0.9}
          />
        )}
      </svg>

      {/* X-axis tick labels */}
      <div
        aria-hidden="true"
        className="mt-1.5 flex justify-between px-2 font-mono text-[10px] text-muted"
      >
        {tickIndices.map((idx) => (
          <span key={idx} className="tabular-nums">
            {formatDate(series[idx]?.date ?? '', locale)}
          </span>
        ))}
      </div>

      {/* Y-axis tick labels (right-rail) */}
      <div className="pointer-events-none absolute right-0 top-0 h-[140px] w-8" aria-hidden="true">
        {yTicks.map((t, i) => (
          <span
            key={i}
            className="absolute right-0 -translate-y-1/2 font-mono text-[10px] tabular-nums text-muted"
            style={{ top: `${String(t.topPct)}%` }}
          >
            {t.value.toLocaleString(locale)}
          </span>
        ))}
      </div>

      {/* Tooltip on hover */}
      {hoveredPoint && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none absolute top-0 -translate-x-1/2 -translate-y-[110%] whitespace-nowrap rounded-md border border-white/10 bg-black/85 px-2 py-1 text-caption text-primary tabular-nums shadow-md backdrop-blur"
          style={{ left: `${String(tooltipLeftPct)}%` }}
        >
          {formatDate(hoveredPoint.date, locale)} · {hoveredPoint.value.toLocaleString(locale)}{' '}
          generation
          {hoveredPoint.value === 1 ? '' : 's'}
        </div>
      )}
    </div>
  );
}

// ── Two-column: plan card + devices card ─────────────────────────────────────

function PlanAndDevicesSection({
  plan,
  cycleResetDate,
}: {
  plan: PlanTier;
  cycleResetDate: string | null;
}) {
  return (
    <Section id="plan-and-devices">
      <div className="grid grid-cols-1 gap-12 standard:grid-cols-2 standard:gap-16">
        <PlanCard plan={plan} cycleResetDate={cycleResetDate} />
        <DevicesCard />
      </div>
    </Section>
  );
}

function PlanCard({ plan, cycleResetDate }: { plan: PlanTier; cycleResetDate: string | null }) {
  const { t } = useTranslation('dashboard');
  const locale = usePreferencesStore((s) => s.locale);
  const details = getPlanDetails(plan);

  const features: React.ReactNode[] = [
    <Trans
      key="tokens"
      i18nKey="plan.feature.tokensPerMonth"
      t={t}
      values={{ tokens: details.tokens.toLocaleString(locale) }}
      components={{ b: <span className="font-medium text-primary" /> }}
    />,
    <Trans
      key="presets"
      i18nKey={plan === 'starter' ? 'plan.feature.presetsStarter' : 'plan.feature.presetsAll'}
      t={t}
      components={{ b: <span className="font-medium text-primary" /> }}
    />,
    details.overage ? (
      <Trans
        key="overage"
        i18nKey="plan.feature.overage"
        t={t}
        components={{ b: <span className="font-medium text-primary" /> }}
      />
    ) : (
      <Trans
        key="no-overage"
        i18nKey="plan.feature.noOverage"
        t={t}
        components={{ b: <span className="font-medium text-primary" /> }}
      />
    ),
    <Trans
      key="support"
      i18nKey="plan.feature.support"
      t={t}
      components={{ b: <span className="font-medium text-primary" /> }}
    />,
  ];

  const priceDollars = details.price / 100;
  const priceFormatted = `$${Number.isInteger(priceDollars) ? priceDollars : priceDollars.toFixed(2)}`;

  return (
    <div className="flex flex-col">
      <p className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
        {t('plan.section')}
      </p>
      <p className="mt-3 font-sans text-[40px] font-light leading-none tracking-[-0.025em] text-primary tabular-nums">
        {details.name}
        <span className="ml-3 align-[6px] font-mono text-[14px] text-muted">
          <span className="font-medium text-secondary">{priceFormatted}</span>
          <span className="ml-1 text-muted">/mo</span>
        </span>
      </p>
      <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.1em] text-muted">
        {t('plan.monthly')}
        {cycleResetDate && (
          <>
            <span className="mx-2 text-muted/50">·</span>
            {t('plan.nextChargeOn', { date: formatDate(cycleResetDate, locale) })}
          </>
        )}
      </p>

      <ul className="m-0 mt-6 flex list-none flex-col gap-2.5 p-0">
        {features.map((feat, i) => (
          <li
            key={i}
            className="grid grid-cols-[16px_1fr] items-baseline gap-2.5 text-[13px] leading-[1.4] text-secondary"
          >
            <span
              aria-hidden="true"
              className="mt-[6px] block h-1.5 w-1.5 rounded-full bg-white/[0.18]"
            />
            <span>{feat}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Link
          to="/billing"
          className="inline-flex items-center gap-2 rounded-md border border-border-bright bg-transparent px-3.5 py-2 text-[13px] font-medium text-primary transition-colors hover:bg-white/[0.03] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          {t('plan.manage')}
        </Link>
        <Link
          to="/billing"
          className="inline-flex items-center gap-2 px-1 py-2 text-[13px] text-secondary transition-colors hover:text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          {t('plan.viewInvoices')}
        </Link>
      </div>
    </div>
  );
}

// ── Devices card (wired to real /v1/api-keys) ────────────────────────────────

interface ApiKeyRecord {
  keyId: string;
  prefix: string;
  deviceName: string;
  createdAt: string;
  lastUsed: string;
  revoked: boolean;
}

function DevicesCard() {
  const { fetchApi } = useApiClient();
  const { userId } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['api-keys', userId ?? 'anon'] as const,
    queryFn: async () => {
      const res = await fetchApi('/v1/api-keys');
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const body = (await res.json()) as {
        data: { keys: ApiKeyRecord[]; activeCount: number; maxKeys: number };
      };
      return body.data;
    },
    enabled: Boolean(userId),
    staleTime: 60_000,
  });

  const allKeys = data?.keys ?? [];
  const activeKeys = allKeys.filter((k) => !k.revoked);
  const visible = activeKeys.slice(0, 3);

  return (
    <div className="flex flex-col">
      <header className="flex items-baseline justify-between">
        <h3 className="m-0 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Connected devices
        </h3>
        {data && (
          <span className="font-mono text-[11px] tracking-[0.04em] text-muted">
            {activeKeys.length} / {data.maxKeys} active
          </span>
        )}
      </header>

      <div className="mt-5 flex flex-1 flex-col">
        {isLoading && (
          <div aria-busy="true" aria-label="Loading connected devices" className="flex flex-col">
            {[0, 1].map((i) => (
              <div
                key={i}
                className={`grid grid-cols-[8px_1fr_auto] items-center gap-3.5 py-3.5 ${
                  i === 0 ? '' : 'border-t border-white/[0.06]'
                }`}
              >
                <Skeleton className="h-1.5 w-1.5 rounded-full" />
                <div className="min-w-0">
                  <Skeleton className="h-3.5 w-32" />
                  <Skeleton className="mt-1.5 h-3 w-48" />
                </div>
              </div>
            ))}
          </div>
        )}
        {isError && (
          <p className="text-[13px] text-accent-red">
            Couldn't load your devices. Try again later.
          </p>
        )}
        {!isLoading && !isError && visible.length === 0 && (
          <p className="text-[13px] text-secondary">
            No devices connected yet. Run <Inline>agentbrush-mcp init</Inline> in your terminal to
            authenticate — it opens your browser to approve the connection and registers this
            device.
          </p>
        )}
        {visible.map((key, i) => (
          <div
            key={key.keyId}
            className={`grid grid-cols-[8px_1fr_auto] items-center gap-3.5 py-3.5 ${
              i === 0 ? '' : 'border-t border-white/[0.06]'
            }`}
          >
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-accent-lime" />
            <div className="min-w-0">
              <p className="m-0 truncate text-[13px] leading-[1.3] text-primary">
                {key.deviceName || 'Unnamed device'}
              </p>
              <p className="m-0 mt-1 flex flex-wrap items-center gap-3 font-mono text-[12px] leading-[1.4] text-muted">
                <span className="rounded-[3px] border border-white/[0.06] bg-[#0c0c0c] px-1.5 py-0.5 text-secondary">
                  {key.prefix}…
                </span>
                <span>last used {formatRelative(key.lastUsed)}</span>
              </p>
            </div>
          </div>
        ))}
      </div>

      <Link
        to="/account#api-keys"
        className="mt-5 inline-flex items-center gap-2 self-start px-1 py-2 text-[13px] text-secondary transition-colors hover:text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
      >
        Manage devices →
      </Link>
    </div>
  );
}

function Inline({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded-[3px] border border-white/[0.06] bg-[#0c0c0c] px-1.5 py-0.5 font-mono text-[12px] text-accent-cyan">
      {children}
    </code>
  );
}

// ── Recent generations ledger (wired to /v1/generations/recent) ──────────────

interface LedgerDay {
  label: string;
  rows: GenerationRecord[];
}

function groupByDay(records: GenerationRecord[]): LedgerDay[] {
  if (records.length === 0) return [];
  const now = Date.now();
  const today = startOfDay(now);
  const yesterday = today - 86_400_000;
  const days = new Map<string, LedgerDay>();

  for (const r of records) {
    const ts = new Date(r.timestamp).getTime();
    const day = startOfDay(ts);
    let label: string;
    if (day === today) label = 'Today';
    else if (day === yesterday) label = 'Yesterday';
    else label = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(day);
    const existing = days.get(label);
    if (existing) existing.rows.push(r);
    else days.set(label, { label, rows: [r] });
  }
  return Array.from(days.values());
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

const LEDGER_INITIAL_LIMIT = 10;
const LEDGER_PAGE_STEP = 10;
// Matches the server-side cap from gen-history MAX_HISTORY. Loading past it
// just returns the same cap, so we stop offering "Load more" at that point.
const LEDGER_MAX_LIMIT = 100;

function LedgerSection() {
  const { t } = useTranslation('dashboard');
  const locale = usePreferencesStore((s) => s.locale);
  const [limit, setLimit] = useState(LEDGER_INITIAL_LIMIT);
  const { data, isLoading, isError, isFetching } = useGenerationsQuery({ limit });

  const rows = data ?? [];
  const days = useMemo(() => groupByDay(rows), [rows]);
  const isEmpty = !isLoading && !isError && days.length === 0;
  // "There's more on the server" = the last fetch filled the page AND we
  // haven't hit the server-side history cap. When the fetch returns fewer
  // rows than requested, the user has seen everything.
  const canLoadMore = rows.length >= limit && limit < LEDGER_MAX_LIMIT;
  const isLoadingMore = isFetching && !isLoading;

  return (
    <Section
      id="ledger"
      data-testid={isEmpty ? 'ledger-empty' : 'ledger-section'}
      aria-labelledby="ledger-section-heading"
    >
      <SectionHeader
        title={<span id="ledger-section-heading">{t('ledger.section')}</span>}
        meta={t('ledger.subtitle')}
      />
      {isLoading && (
        <div aria-busy="true" aria-label="Loading recent generations" className="flex flex-col">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="grid grid-cols-[12px_1fr_auto] items-center gap-5 border-b border-white/[0.06] py-4 standard:grid-cols-[12px_1fr_140px_90px_72px]"
            >
              <Skeleton className="h-2 w-2 rounded-[2px]" />
              <div>
                <Skeleton className="h-3.5 w-32" />
                <Skeleton className="mt-1.5 h-2.5 w-20" />
              </div>
              <Skeleton className="hidden h-3 w-20 standard:block" />
              <Skeleton className="hidden h-3 w-16 standard:block" />
              <Skeleton className="h-3 w-12 justify-self-end" />
            </div>
          ))}
        </div>
      )}
      {isError && (
        <p className="text-[14px] text-accent-red">
          Couldn't load your recent generations. Try again later.
        </p>
      )}
      {isEmpty && <p className="text-[14px] text-secondary">{t('ledger.empty')}</p>}
      {!isLoading && !isError && days.length > 0 && (
        <div className="flex flex-col">
          {days.map((day, dayIdx) => (
            <div key={day.label}>
              <div
                className={`flex items-baseline justify-between border-b border-white/[0.06] pb-3 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted ${
                  dayIdx === 0 ? 'pt-0' : 'pt-7'
                }`}
              >
                <span>{day.label}</span>
                <span className="text-secondary">
                  {t('ledger.daySummary', {
                    count: day.rows.length,
                    tokens: day.rows.reduce((a, r) => a + r.tokenCost, 0).toLocaleString(locale),
                  })}
                </span>
              </div>
              {day.rows.map((row) => (
                <LedgerRow key={row.id} row={row} />
              ))}
            </div>
          ))}
        </div>
      )}
      {!isError && canLoadMore && (
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={() => setLimit((prev) => Math.min(prev + LEDGER_PAGE_STEP, LEDGER_MAX_LIMIT))}
            disabled={isLoadingMore}
            data-testid="ledger-load-more"
            className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-4 py-2 font-mono text-[12px] uppercase tracking-[0.08em] text-secondary transition-colors hover:border-white/20 hover:bg-white/[0.05] hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 disabled:cursor-default disabled:opacity-60"
          >
            {isLoadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </Section>
  );
}

function LedgerRow({ row }: { row: GenerationRecord }) {
  const presetTone = PRESET_TONE[row.preset] ?? 'bg-white/[0.18]';
  const presetName = PRESET_DISPLAY_NAME[row.preset] ?? row.preset;
  const time = new Date(row.timestamp).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="grid grid-cols-[12px_1fr_auto] items-center gap-5 border-b border-white/[0.06] py-4 standard:grid-cols-[12px_1fr_140px_90px_72px]">
      <span aria-hidden="true" className={`h-2 w-2 rounded-[2px] ${presetTone}`} />
      <div className="min-w-0">
        <p className="m-0 truncate font-mono text-[13px] leading-[1.3] text-primary">{row.id}</p>
        <p className="m-0 mt-1 truncate font-mono text-[11px] leading-[1.3] text-muted/70">
          {row.promptHash.slice(0, 12)}
        </p>
      </div>
      <span className="hidden standard:block text-[12px] text-secondary">{presetName}</span>
      <span
        className={`hidden standard:inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.04em] ${
          row.qualityHint === 'high' ? 'text-primary' : 'text-muted'
        }`}
      >
        <span
          aria-hidden="true"
          className={`h-1 w-1 rounded-full ${
            row.qualityHint === 'high' ? 'bg-accent-lime' : 'bg-white/[0.18]'
          }`}
        />
        {row.qualityHint}
      </span>
      <span className="text-right font-mono text-[13px] tabular-nums text-primary">
        {row.tokenCost}
        <span className="ml-1 text-muted">t · {time}</span>
      </span>
    </div>
  );
}

// ── Footer ───────────────────────────────────────────────────────────────────

function DashboardFooter() {
  const { t } = useTranslation('dashboard');
  const { t: tl } = useTranslation('legal');
  return (
    <footer className="mt-20 flex items-center justify-between border-t border-white/[0.06] py-6 font-mono text-[11px] text-muted">
      <span>{t('footer.version', { build: 'a4f12c' })}</span>
      <nav className="flex flex-wrap gap-x-5 gap-y-2">
        <Link className="text-muted hover:text-secondary" to="/impressum">
          {tl('links.impressum')}
        </Link>
        <Link className="text-muted hover:text-secondary" to="/privacy">
          {tl('links.privacyPolicy')}
        </Link>
        <Link className="text-muted hover:text-secondary" to="/terms">
          {tl('links.termsOfService')}
        </Link>
        <Link className="text-muted hover:text-secondary" to="/cookie-policy">
          {tl('links.cookiePolicy')}
        </Link>
        <Link className="text-muted hover:text-secondary" to="/contact">
          {tl('links.contact')}
        </Link>
        <Link className="text-muted hover:text-secondary" to="/docs">
          {t('footer.support')}
        </Link>
      </nav>
    </footer>
  );
}

// ── Empty (never-generated) onboarding view ──────────────────────────────────

function DashboardEmptyOnboarding({ firstName }: { firstName: string }) {
  const { t } = useTranslation('dashboard');
  return (
    <Section first>
      <SectionEyebrow>{t('eyebrow')}</SectionEyebrow>
      <h1 className="mt-3 max-w-[18ch] font-sans text-[clamp(40px,6vw,56px)] font-light leading-[1.05] tracking-[-0.03em] text-primary">
        {firstName ? (
          <>
            {firstName}, <em className="not-italic text-muted">{t('empty.title')}</em>
          </>
        ) : (
          <>{t('empty.title')}</>
        )}
      </h1>
      <p className="mt-6 max-w-[56ch] text-[16px] leading-[1.6] text-secondary">
        {t('empty.lede')}
      </p>

      <div className="mt-12 flex flex-wrap items-center gap-3">
        <Link
          to="/getting-started"
          className="inline-flex items-center gap-2 rounded-md bg-accent-lime px-4 py-2.5 text-[13px] font-semibold text-canvas transition-[filter] hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          Connect your agent →
        </Link>
        <Link
          to="/billing"
          className="inline-flex items-center gap-2 px-1 py-2.5 text-[13px] text-secondary transition-colors hover:text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          Choose a plan →
        </Link>
      </div>
    </Section>
  );
}

// ── ArrowRight ───────────────────────────────────────────────────────────────

function ArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M3 7h8m0 0L7 3m4 4l-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function useDaysUntil(iso: string | null): number {
  return useMemo(() => {
    if (!iso) return 0;
    const target = new Date(iso).getTime();
    if (Number.isNaN(target)) return 0;
    const ms = target - Date.now();
    return Math.max(0, Math.ceil(ms / 86_400_000));
  }, [iso]);
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  const diff = Date.now() - d.getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'just now';
}
